/**
 * Chase camera — Rocket League's ball cam and car cam.
 *
 * All of the geometry below is done in the PHYSICS frame (right-handed, +Z up,
 * see src/physics/math.ts). The conversion to Three.js happens exactly once per
 * update, at the very bottom of `writeToCamera`, through ./convert. Nothing in
 * this file swaps an axis by hand.
 *
 * The camera is a boom: a horizontal unit direction (`boom`, the direction the
 * camera looks in plan view) anchored at a `pivot` that trails the car.
 *
 *     cameraPos = pivot - boom * distance + worldUp * height
 *
 * In ball cam the boom points from the car toward the ball, so the camera ends
 * up on the far side of the car from the ball and the view contains both. In
 * car cam the boom is the car's forward axis flattened into the world plane —
 * flattened, not the raw forward axis, which is what keeps the view readable
 * when the car drives up a wall.
 *
 * SMOOTHING IS FRAME-RATE INDEPENDENT AND MUST STAY THAT WAY. Every smoothed
 * quantity uses `smoothFactor(k, dt) = 1 - exp(-k * dt)`. The naive
 * `x += (target - x) * 0.1` per frame is a different filter at every refresh
 * rate: 0.1 per frame at 60 Hz has a 158 ms time constant, at 144 Hz it has
 * 66 ms, so the game literally plays differently on a better monitor. With the
 * exponential form, smoothing toward a stationary target for one second lands
 * on the same value whether that second was cut into 60 pieces or 144 — the
 * per-step factors telescope into exp(-k * total_time) exactly.
 *
 * This is per-FRAME state, driven from `render(alpha, frameTime)` in
 * src/render/loop.ts, NOT from the fixed 120 Hz tick. Feeding it `DT` would peg it
 * back to the tick rate and defeat the point.
 */

import { PerspectiveCamera, Vector3 } from 'three';
import { ARENA, CAR_MAX_SPEED, SUPERSONIC_THRESHOLD } from '../physics/constants';
import type { BallState } from '../physics/ball';
import type { CarState } from '../physics/car-state';
import {
  clamp,
  forward as carForward,
  length,
  set,
  type Vec3,
  vec3,
} from '../physics/math';
import { toThree } from './convert';

const DEG = Math.PI / 180;

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * The in-game "Camera Settings" panel, one field per slider.
 *
 * SOURCING: the ranges (Distance 100-400, Height 40-200, Angle -15..0,
 * Stiffness 0-1, Swivel Speed 1-10, Transition Speed 0.5-2, FOV 60-110) are
 * stable across every published description of the panel. The defaults below
 * (Distance 270, Height 100, Angle -3.00, Stiffness 0.45, Swivel Speed 2.50,
 * Transition Speed 1.00, FOV 110) are the shipped in-game defaults.
 *
 * Note that these are the SLIDER values only. How the three rate-like sliders
 * (Stiffness, Swivel Speed, Transition Speed) map onto actual smoothing rates is
 * a separate question with no published answer — see the
 * [UNVERIFIED-ESTIMATE] tags on the tuning constants further down.
 */
export interface CameraSettings {
  /** Boom length in uu, measured horizontally from the pivot. */
  distance: number;
  /** Height of the camera above the pivot, in uu. */
  height: number;
  /** Camera pitch in DEGREES; negative looks down. Car cam only — see below. */
  angle: number;
  /** 0 = floaty pivot, 1 = rigidly bolted to the car. */
  stiffness: number;
  /** How fast the boom swings around to its target direction. */
  swivelSpeed: number;
  /** Multiplier on the swivel rate during a ball-cam/car-cam transition. */
  transitionSpeed: number;
  /** HORIZONTAL field of view in degrees — see `horizontalToVerticalFov`. */
  fov: number;
}

export interface CameraSettingRange {
  min: number;
  max: number;
  default: number;
}

export const CAMERA_SETTING_RANGES: { readonly [K in keyof CameraSettings]: CameraSettingRange } = {
  distance: { min: 100, max: 400, default: 270 },
  height: { min: 40, max: 200, default: 100 },
  angle: { min: -15, max: 0, default: -3 },
  stiffness: { min: 0, max: 1, default: 0.45 },
  swivelSpeed: { min: 1, max: 10, default: 2.5 },
  transitionSpeed: { min: 0.5, max: 2, default: 1.0 },
  fov: { min: 60, max: 110, default: 110 },
} as const;

const SETTING_KEYS = Object.keys(CAMERA_SETTING_RANGES) as (keyof CameraSettings)[];

export const defaultCameraSettings = (): CameraSettings => ({
  distance: CAMERA_SETTING_RANGES.distance.default,
  height: CAMERA_SETTING_RANGES.height.default,
  angle: CAMERA_SETTING_RANGES.angle.default,
  stiffness: CAMERA_SETTING_RANGES.stiffness.default,
  swivelSpeed: CAMERA_SETTING_RANGES.swivelSpeed.default,
  transitionSpeed: CAMERA_SETTING_RANGES.transitionSpeed.default,
  fov: CAMERA_SETTING_RANGES.fov.default,
});

/**
 * Merge a partial settings object over `base`, clamping every field to its
 * documented range. Non-finite input falls back to the default rather than
 * propagating a NaN into the view matrix, where it would blank the screen with
 * no error anywhere.
 */
export const clampCameraSettings = (
  patch: Partial<CameraSettings>,
  base: CameraSettings = defaultCameraSettings()
): CameraSettings => {
  const out: CameraSettings = { ...base };
  for (const key of SETTING_KEYS) {
    const range = CAMERA_SETTING_RANGES[key];
    const v = patch[key] ?? out[key];
    out[key] = Number.isFinite(v) ? clamp(v, range.min, range.max) : range.default;
  }
  return out;
};

// ---------------------------------------------------------------------------
// Tuning constants — none of these are published Rocket League values
// ---------------------------------------------------------------------------

/**
 * Stiffness maps onto the pivot's exponential rate (1/s). The endpoints are
 * chosen so the default 0.45 gives a ~68 ms time constant, which reads as
 * "attached but not welded". [UNVERIFIED-ESTIMATE: Psyonix publishes no units
 * for the Stiffness slider; only its 0-1 range is known.]
 */
const STIFFNESS_RATE_MIN = 3;
const STIFFNESS_RATE_MAX = 32;

/** Swivel Speed 1-10 maps to a boom rate of 2-20 1/s. [UNVERIFIED-ESTIMATE] */
const SWIVEL_RATE_SCALE = 2;

/** The aimed-at point tracks a little faster than the boom swings. */
const AIM_RATE_SCALE = 2.5;

/** How fast the "we just toggled ball cam" weight falls off, 1/s. */
const TRANSITION_DECAY = 4;

/** Extra degrees of FOV at CAR_MAX_SPEED, and again for supersonic. */
export const FOV_SPEED_BONUS = 5;
export const FOV_SUPERSONIC_BONUS = 3;
/** Total dynamic widening at max speed while supersonic. */
export const FOV_MAX_BONUS = FOV_SPEED_BONUS + FOV_SUPERSONIC_BONUS;

/** FOV lerps toward its target at this rate (1/s) so it never pops. */
const FOV_RATE = 6;

/** Shake noise sample rate (Hz) and how much of it is applied vertically. */
const SHAKE_FREQUENCY = 22;
const SHAKE_VERTICAL_SCALE = 0.6;
/** Fraction of the shake offset also applied to the aim point, so a shake
 *  rotates the view a little instead of only sliding it. */
const SHAKE_AIM_FACTOR = 0.35;
const SHAKE_DEFAULT_DURATION = 0.35;
const DEFAULT_SHAKE_SEED = 0x9e3779b9;

/** Keep the camera out of the floor and the ceiling. */
const FLOOR_MARGIN = 20;
const CEILING_MARGIN = 20;

/** How far in front the car-cam look target is placed. Only its direction
 *  matters to `lookAt`; the magnitude just needs to be well away from zero. */
const LOOK_TARGET_RANGE = 1000;

/** Longest frame we will integrate in one go — same guard as the sim loop. */
const MAX_FRAME_TIME = 0.25;

const EPS = 1e-9;
const TWO_PI = Math.PI * 2;

/** Wrap to (-pi, pi] so an angular difference always takes the short way round. */
const wrapPi = (a: number): number => {
  const w = ((a + Math.PI) % TWO_PI + TWO_PI) % TWO_PI;
  return w - Math.PI;
};

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Fraction of the remaining distance to cover this step for an exponential
 * filter with rate `k` (1/s). Frame-rate independent by construction: the
 * complements multiply, so N steps of dt give exactly exp(-k * N * dt).
 */
export const smoothFactor = (k: number, dt: number): number => 1 - Math.exp(-k * dt);

/**
 * Rocket League's FOV slider is HORIZONTAL; Three.js's `PerspectiveCamera.fov`
 * is VERTICAL. Feeding 110 straight into Three gives a wildly wider view than
 * the game at any normal aspect ratio, so the conversion is mandatory.
 */
export const horizontalToVerticalFov = (hFovDeg: number, aspect: number): number => {
  const a = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  return (2 * Math.atan(Math.tan((hFovDeg * DEG) / 2) / a)) / DEG;
};

/**
 * Speed -> horizontal FOV, in RL slider space. Deliberately subtle: a linear
 * ramp to +FOV_SPEED_BONUS at CAR_MAX_SPEED, plus a second ramp that only opens
 * once the supersonic flag is set and reaches +FOV_SUPERSONIC_BONUS at the speed
 * cap. Both terms are non-decreasing in speed, and the supersonic term starts
 * from zero at SUPERSONIC_THRESHOLD, so the mapping is monotonic AND continuous
 * across the moment the flag flips — a step there would be visible as a snap.
 * [UNVERIFIED-ESTIMATE: the game's speed-FOV kick is not documented anywhere;
 * these magnitudes are chosen by feel.]
 */
export const fovForSpeed = (baseFov: number, speed: number, supersonic: boolean): number => {
  const t = clamp(speed / CAR_MAX_SPEED, 0, 1);
  const superT = supersonic
    ? clamp((speed - SUPERSONIC_THRESHOLD) / (CAR_MAX_SPEED - SUPERSONIC_THRESHOLD), 0, 1)
    : 0;
  return baseFov + FOV_SPEED_BONUS * t + FOV_SUPERSONIC_BONUS * superT;
};

/**
 * Seeded integer hash -> [-1, 1]. `Math.random` is banned here: a camera that
 * cannot be replayed cannot be tested, and shake would desync a replay.
 */
const hashToUnit = (n: number, seed: number): number => {
  let x = (n ^ seed) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d);
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b);
  x = (x ^ (x >>> 16)) >>> 0;
  return (x / 0xffffffff) * 2 - 1;
};

/**
 * Value noise in one dimension, smoothstep-interpolated between integer
 * samples. Continuous in `t`, which is what makes shake look the same at any
 * frame rate — a per-frame random draw would visibly get busier at 144 Hz.
 */
const smoothNoise = (t: number, channel: number, seed: number): number => {
  const i = Math.floor(t);
  const f = t - i;
  const w = f * f * (3 - 2 * f);
  const a = hashToUnit(i * 3 + channel, seed);
  const b = hashToUnit((i + 1) * 3 + channel, seed);
  return a + (b - a) * w;
};

// ---------------------------------------------------------------------------
// GameCamera
// ---------------------------------------------------------------------------

export interface GameCameraOptions {
  camera?: PerspectiveCamera;
  settings?: Partial<CameraSettings>;
  /** Seed for the shake noise. Same seed + same inputs = same output. */
  seed?: number;
}

const _v0 = new Vector3();
const _v1 = new Vector3();

export class GameCamera {
  /** The Three.js camera this drives. Overwritten on every `update`. */
  readonly camera: PerspectiveCamera;

  /** Final camera position, PHYSICS frame. Treat as read-only. */
  readonly position: Vec3 = vec3();
  /** Final look target, PHYSICS frame. Treat as read-only. */
  readonly target: Vec3 = vec3();
  /** Shake displacement currently folded into `position`. Treat as read-only. */
  readonly shakeOffset: Vec3 = vec3();

  private settings_: CameraSettings;
  private readonly seed: number;

  /** Smoothed anchor point — trails the car, frozen while it is demolished. */
  private readonly pivot: Vec3 = vec3();
  /**
   * The boom is smoothed as a YAW ANGLE, not as a lerped-then-renormalized
   * direction vector. Lerping vectors is degenerate at exactly 180 degrees: the
   * interpolant passes through the origin, renormalizing snaps it straight back
   * to where it started, and the camera silently locks — which is precisely the
   * ball-directly-behind-the-car case that a ball-cam toggle produces. Angles
   * have no such hole, and the filter stays linear so it stays exactly
   * frame-rate independent.
   */
  private boomYaw = 0;
  /** Unit horizontal look direction, derived from `boomYaw`. */
  private readonly boom: Vec3 = vec3(1, 0, 0);
  /** Smoothed point the camera aims at in ball cam. */
  private readonly aim: Vec3 = vec3();

  private fovCurrent: number;
  private appliedVerticalFov = -1;

  private shakeAmplitude = 0;
  private shakeRemaining = 0;
  private shakeDuration = 0;
  private shakePhase = 0;

  private transitionBlend = 0;
  private lastBallCam = true;
  private initialized = false;

  constructor(options: GameCameraOptions = {}) {
    this.settings_ = clampCameraSettings(options.settings ?? {});
    this.seed = options.seed ?? DEFAULT_SHAKE_SEED;
    this.camera =
      options.camera ??
      new PerspectiveCamera(
        horizontalToVerticalFov(this.settings_.fov, 16 / 9),
        16 / 9,
        10,
        30000
      );
    this.fovCurrent = this.settings_.fov;
  }

  get settings(): Readonly<CameraSettings> {
    return this.settings_;
  }

  /** Apply a settings patch, clamped to the documented ranges. */
  setSettings(patch: Partial<CameraSettings>): void {
    this.settings_ = clampCameraSettings(patch, this.settings_);
  }

  /** Current HORIZONTAL fov in RL slider space, after the speed widening. */
  get fov(): number {
    return this.fovCurrent;
  }

  /**
   * Kick the camera. `intensity` is a displacement in uu; a weaker shake never
   * interrupts a stronger one still in progress.
   */
  shake(intensity: number, duration: number = SHAKE_DEFAULT_DURATION): void {
    if (!Number.isFinite(intensity) || intensity <= 0 || duration <= 0) return;
    const active = this.shakeRemaining > 0 ? this.shakeAmplitude : 0;
    if (intensity < active) return;
    this.shakeAmplitude = intensity;
    this.shakeDuration = duration;
    this.shakeRemaining = duration;
  }

  /** Snap every smoothed quantity to its target — spawn, kickoff, replay seek. */
  reset(car: CarState, ball: BallState, ballCamEnabled: boolean): void {
    this.pivot.x = car.position.x;
    this.pivot.y = car.position.y;
    this.pivot.z = car.position.z;

    this.boomYaw = this.desiredBoomYaw(car, ball, ballCamEnabled);
    this.syncBoom();

    this.aim.x = ball.position.x;
    this.aim.y = ball.position.y;
    this.aim.z = ball.position.z;

    this.fovCurrent = fovForSpeed(this.settings_.fov, this.carSpeed(car), car.supersonic);
    this.shakeAmplitude = 0;
    this.shakeRemaining = 0;
    this.shakeDuration = 0;
    this.shakePhase = 0;
    this.transitionBlend = 0;
    this.lastBallCam = ballCamEnabled;
    this.initialized = true;

    this.writeToCamera(car, ballCamEnabled);
  }

  /**
   * Advance and write into the Three.js camera. `frameTime` is the real
   * wall-clock frame delta in seconds, not the physics tick.
   */
  update(car: CarState, ball: BallState, ballCamEnabled: boolean, frameTime: number): void {
    if (!this.initialized) {
      this.reset(car, ball, ballCamEnabled);
      return;
    }

    const dt = Number.isFinite(frameTime) ? clamp(frameTime, 0, MAX_FRAME_TIME) : 0;

    if (ballCamEnabled !== this.lastBallCam) {
      this.lastBallCam = ballCamEnabled;
      this.transitionBlend = 1;
    }

    const s = this.settings_;

    // Transition Speed is a multiplier that only bites while the boom target is
    // still swinging from a mode switch; at the default 1.0 it is a no-op, which
    // is why it can be folded in unconditionally.
    // [UNVERIFIED-ESTIMATE: the slider's exact in-game semantics are undocumented.]
    const transitionMul = 1 + this.transitionBlend * (s.transitionSpeed - 1);

    const pivotRate = STIFFNESS_RATE_MIN + (STIFFNESS_RATE_MAX - STIFFNESS_RATE_MIN) * s.stiffness;
    const kPivot = smoothFactor(pivotRate, dt);
    const kBoom = smoothFactor(SWIVEL_RATE_SCALE * s.swivelSpeed * transitionMul, dt);
    const kAim = smoothFactor(AIM_RATE_SCALE * s.swivelSpeed * transitionMul, dt);

    // A demolished car has no position worth following, so the pivot holds
    // where it was and the boom keeps swinging toward the ball — you keep
    // watching the play from where you died.
    if (!car.demolished) {
      this.pivot.x += (car.position.x - this.pivot.x) * kPivot;
      this.pivot.y += (car.position.y - this.pivot.y) * kPivot;
      this.pivot.z += (car.position.z - this.pivot.z) * kPivot;
    }

    const desiredYaw = this.desiredBoomYaw(car, ball, ballCamEnabled);
    this.boomYaw += wrapPi(desiredYaw - this.boomYaw) * kBoom;
    this.syncBoom();

    this.aim.x += (ball.position.x - this.aim.x) * kAim;
    this.aim.y += (ball.position.y - this.aim.y) * kAim;
    this.aim.z += (ball.position.z - this.aim.z) * kAim;

    const targetFov = fovForSpeed(s.fov, this.carSpeed(car), car.supersonic);
    this.fovCurrent += (targetFov - this.fovCurrent) * smoothFactor(FOV_RATE, dt);

    // Shake advances on accumulated TIME, not on a frame counter, so its
    // waveform is identical at any refresh rate.
    if (this.shakeRemaining > 0) {
      this.shakeRemaining = Math.max(0, this.shakeRemaining - dt);
      this.shakePhase += dt * SHAKE_FREQUENCY;
      if (this.shakeRemaining === 0) this.shakeAmplitude = 0;
    }

    this.transitionBlend *= Math.exp(-TRANSITION_DECAY * dt);

    this.writeToCamera(car, ballCamEnabled);
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private carSpeed(car: CarState): number {
    return car.demolished ? 0 : length(car.velocity);
  }

  private syncBoom(): void {
    this.boom.x = Math.cos(this.boomYaw);
    this.boom.y = Math.sin(this.boomYaw);
    this.boom.z = 0;
  }

  /**
   * Yaw the boom wants to point at, in radians.
   *
   * Ball cam: pivot -> ball, so the camera lands on the opposite side of the
   * car and both are in frame. Car cam: the car's forward axis FLATTENED into
   * the world plane. Flattening is what makes wall driving readable — following
   * the car's actual forward axis up a wall would pitch the camera at the sky.
   * Degenerate inputs (car exactly at the ball, car nose straight up a wall)
   * hold the current yaw, which is the only answer that cannot produce a NaN or
   * a spin.
   */
  private desiredBoomYaw(car: CarState, ball: BallState, ballCamEnabled: boolean): number {
    let dx: number;
    let dy: number;
    if (ballCamEnabled || car.demolished) {
      dx = ball.position.x - this.pivot.x;
      dy = ball.position.y - this.pivot.y;
    } else {
      const f = carForward(car.orientation);
      dx = f.x;
      dy = f.y;
    }
    const len = Math.hypot(dx, dy);
    if (!Number.isFinite(len) || len < EPS) return this.boomYaw;
    return Math.atan2(dy, dx);
  }

  private computeShake(out: Vec3): void {
    if (this.shakeRemaining <= 0 || this.shakeDuration <= 0) {
      set(out, 0, 0, 0);
      return;
    }
    // Quadratic falloff, exactly zero at the end of the window.
    const decay = this.shakeRemaining / this.shakeDuration;
    const amp = this.shakeAmplitude * decay * decay;
    const t = this.shakePhase;
    out.x = amp * smoothNoise(t, 0, this.seed);
    out.y = amp * smoothNoise(t, 1, this.seed);
    out.z = amp * smoothNoise(t, 2, this.seed) * SHAKE_VERTICAL_SCALE;
  }

  private writeToCamera(car: CarState, ballCamEnabled: boolean): void {
    const s = this.settings_;
    this.computeShake(this.shakeOffset);

    // Boom is horizontal, so `height` is a pure world-up offset and `distance`
    // is a pure horizontal setback — neither is contaminated by the car's roll.
    const px = this.pivot.x - this.boom.x * s.distance + this.shakeOffset.x;
    const py = this.pivot.y - this.boom.y * s.distance + this.shakeOffset.y;
    let pz = this.pivot.z + s.height + this.shakeOffset.z;
    pz = clamp(pz, ARENA.floor + FLOOR_MARGIN, ARENA.ceiling - CEILING_MARGIN);
    set(this.position, px, py, pz);

    const ballFocus = ballCamEnabled || car.demolished;
    if (ballFocus) {
      // Ball cam centres the ball, full stop. The Angle setting is deliberately
      // NOT applied here: tilting the view in ball cam would push the ball off
      // centre, which is the one thing ball cam exists to prevent.
      // [UNVERIFIED: whether the shipped game leaves a residual tilt in ball cam.]
      set(
        this.target,
        this.aim.x + this.shakeOffset.x * SHAKE_AIM_FACTOR,
        this.aim.y + this.shakeOffset.y * SHAKE_AIM_FACTOR,
        this.aim.z + this.shakeOffset.z * SHAKE_AIM_FACTOR
      );
    } else {
      // Car cam looks along the boom, pitched by exactly `angle` (0 = level
      // horizon). The car sits low in frame because the camera is `height`
      // above it, not because the view is tilted at it.
      const a = s.angle * DEG;
      const ca = Math.cos(a);
      set(
        this.target,
        px + this.boom.x * ca * LOOK_TARGET_RANGE + this.shakeOffset.x * SHAKE_AIM_FACTOR,
        py + this.boom.y * ca * LOOK_TARGET_RANGE + this.shakeOffset.y * SHAKE_AIM_FACTOR,
        pz + Math.sin(a) * LOOK_TARGET_RANGE + this.shakeOffset.z * SHAKE_AIM_FACTOR
      );
    }

    // --- the one and only frame conversion ---
    // World up, never the car's up: rolling the horizon with a car that is
    // driving along a wall makes wall play unreadable. Three's `lookAt` uses
    // `camera.up` as the roll reference, so pinning it to +Y is the whole fix.
    this.camera.up.set(0, 1, 0);
    this.camera.position.copy(toThree(this.position, _v0));
    this.camera.lookAt(toThree(this.target, _v1));

    const vFov = horizontalToVerticalFov(this.fovCurrent, this.camera.aspect);
    if (Math.abs(vFov - this.appliedVerticalFov) > 1e-6) {
      this.appliedVerticalFov = vFov;
      this.camera.fov = vFov;
      this.camera.updateProjectionMatrix();
    }
  }
}
